import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserStore } from 'stores/use-user-store';

import { PUBLIC_BASE_URL_ENV } from 'tools/environments';

import { Button } from 'core/generic/elements/button';
import { Icon } from 'core/generic/elements/icons';

import { NavItemType } from '../../types';
import { buildLoginUrl } from '../../utils/build-login-url';
import mobileNavItemStyles from '../mobile-nav-item/mobile-nav-item.module.scss';
import styles from './mobile-nav.module.scss';

type MobileNavItemProps = {
	item: Partial<NavItemType>;
};

export const MobileNavItem = ({ item }: MobileNavItemProps) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const handleMobileNav = (isOpen: boolean) => {
		setIsOpen(isOpen);
	};

	return (
		<>
			<a
				href={item.url || ''}
				onClick={(e) => {
					if (!item.items || item.items.length <= 0) return;
					e.preventDefault();
					setIsOpen(true);
				}}
				className={`body-large bold`}
				target={item.target}
				aria-label={item.title}
			>
				<span>{item.title}</span>

				{item.items && item.items.length > 0 && (
					<Icon
						variant="ChevronDown"
						className={`${mobileNavItemStyles.chevron} `}
					/>
				)}
			</a>

			{item.items && item.items.length > 0 && (
				<MobileNav
					primary={false}
					items={item.items}
					isOpen={isOpen}
					callback={handleMobileNav}
					direction="Right"
					title={item.title}
				/>
			)}
		</>
	);
};

type MobileNavProps = {
	items: NavItemType[];
	isOpen: boolean;
	callback: (isOpen: boolean) => void;
	direction: string;
	primary: boolean;
	title?: string;
};

export const MobileNav = ({
	items,
	isOpen,
	callback,
	direction,
	primary,
	title,
}: MobileNavProps) => {
	const { t } = useTranslation();
	const [showMore, setShowMore] = useState(false);
	const [isNavOpen, setIsNavOpen] = useState<boolean>(isOpen);
	const [hiddenItems, setHiddenItems] = useState<number[]>([]);

	const navRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const { isLoggedIn } = useUserStore((state) => ({
		isLoggedIn: state.user?.isLoggedIn,
	}));

	const [loginUrl, setLoginUrl] = useState(() => buildLoginUrl());

	useEffect(() => {
		setLoginUrl(
			buildLoginUrl(window.location.pathname + window.location.search)
		);
	}, []);

	const closeNav = useCallback(() => {
		callback(false);
	}, [callback]);

	const intersectingCallback = useCallback(
		(
			entries: IntersectionObserverEntry[],
			children?: NodeListOf<Element>
		) => {
			entries.forEach((entry: IntersectionObserverEntry) => {
				const index = children
					? Array.from(children).indexOf(entry.target)
					: 0;

				if (entry.isIntersecting) {
					setHiddenItems((prevItems: number[]) => {
						prevItems.filter((item) => item !== index);
						return prevItems.sort((a: number, b: number) => a - b);
					});
					entry.target.classList.remove(styles.hidden);
				} else {
					setHiddenItems((prevItems: number[]) => {
						if (!prevItems.includes(index)) {
							return [...prevItems, index];
						}
						return prevItems.sort((a: number, b: number) => a - b);
					});
					entry.target.classList.add(styles.hidden);
				}
			});

			if (hiddenItems.length > 0) {
				setShowMore(true);
			}
		},
		[hiddenItems.length]
	);

	useEffect(() => {
		let observer: IntersectionObserver;
		const children = navRef?.current?.querySelectorAll(`li:not(.more)`);

		if (isNavOpen) {
			observer = new IntersectionObserver(
				(entries) => intersectingCallback(entries, children),
				{
					root: navRef.current,
					rootMargin: '0px 0px -40px 0px',
					threshold: 1.0,
				}
			);

			if (navRef.current) {
				children?.forEach((child) => observer.observe(child));
			}
		}

		return () => {
			if (children) {
				children.forEach((child) => observer?.unobserve(child));
			}
		};
	}, [intersectingCallback, isNavOpen]);

	useEffect(() => {
		setHiddenItems([]);
		if (isOpen) {
			setIsNavOpen(isOpen);
		} else {
			setTimeout(() => {
				setIsNavOpen(isOpen);
			}, 500);
		}
	}, [isOpen]);

	return (
		isNavOpen && (
			<div
				className={`${styles.nav} ${
					isOpen
						? styles[`slideIn${direction}`]
						: styles[`slideOut${direction}`]
				} `}
				data-theme="dark"
				ref={containerRef}
				aria-label={
					primary ? 'Mobile Navigation' : `${title} Navigation`
				}
				role="navigation"
			>
				{primary && (
					<header className={`${styles.header}`}>
						<span className={`${styles.crest}`}>
							<Icon variant="CityCrest" />
						</span>

						<button
							onClick={closeNav}
							className={styles['close-button']}
							aria-label="Close"
						>
							<Icon variant="Close" />
						</button>
					</header>
				)}
				{!primary && (
					<header className={`${styles.header}`}>
						<button
							onClick={closeNav}
							className={styles['back-button']}
							aria-label="Back"
						>
							<Icon variant="ArrowLeft" />
							<span className={`body-medium bold`}>{title}</span>
						</button>
						<button
							onClick={closeNav}
							className={styles['close-button']}
							aria-label="Close"
						>
							<Icon variant="Close" />
						</button>
					</header>
				)}

				<div className={`${styles['nav-items']}`} ref={navRef}>
					<ul className={`${styles.list}`}>
						{items.map((item, index) => {
							return (
								<li key={index} className={`${styles.item}`}>
									<MobileNavItem item={item} />
								</li>
							);
						})}
						<li
							className={`more ${styles.item} ${styles.more} ${
								!showMore ? styles.hidden : ''
							}`}
						>
							<MobileNavItem
								item={{
									url: '#',
									title: t(
										'global.header:more_items_button',
										'More'
									),
									items: items.filter((_, index) =>
										hiddenItems.includes(index)
									),
								}}
							/>
						</li>
					</ul>
				</div>

				{primary && (
					<footer className={`${styles.footer}`}>
						<ul className={`${styles.list}`}>
							<li>
								<a href="/help" className={`body-medium`}>
									<Icon
										className={styles.icon}
										variant="Help"
									/>
									{t('global.header:help', 'Help')}
								</a>
							</li>
						</ul>

						<div className={`${styles.buttons}`}>
							{isLoggedIn ? (
								<>
									<Button
										href={`${PUBLIC_BASE_URL_ENV}/account`}
										variant="featured"
										label={t(
											'global.header:my_account',
											'My Account'
										)}
										onClick={closeNav}
									/>
									<Button
										href={`${PUBLIC_BASE_URL_ENV}/feed`}
										variant="featured"
										label={t(
											'global.header:cityzens_feed',
											'Cityzens Feed'
										)}
										onClick={closeNav}
									/>
								</>
							) : (
								<>
									<Button
										href={`${PUBLIC_BASE_URL_ENV}/account/register`}
										variant="featured"
										label={t(
											'global.header:register',
											'Register'
										)}
										onClick={closeNav}
									/>
									<Button
										href={loginUrl}
										variant="tertiary"
										label={t(
											'global.header:sign_in',
											'Sign in'
										)}
										onClick={closeNav}
									/>
								</>
							)}
						</div>
					</footer>
				)}
			</div>
		)
	);
};
